import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ApiError } from '../api/errors';
import { Btn, Logo } from '../components/ui';
import { TextField } from '../components/forms';
import { LegalFooter } from '../components/layout';
import { useAuthStore } from '../stores/authStore';
import { c } from '../styles/theme';
import { paths } from './paths';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verifyEmail = useAuthStore((s) => s.verifyEmail);
  const [error, setError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pending, setPending] = useState(false);

  async function handleVerify(event: FormEvent) {
      event.preventDefault();
      setError('');
      const token = searchParams.get('token') ?? '';
      if (!token.trim()) {
        setError('This verification link is not valid');
        return;
      }
      if (newPassword.length < 10) {
        setError('Password must be at least 10 characters');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError("Passwords don't match");
        return;
      }

      setPending(true);
      try {
        await verifyEmail(token, newPassword);
        const { appAccess } = useAuthStore.getState();
        navigate(appAccess === 'granted' ? paths.dashboard : paths.upgrade, {
          replace: true,
        });
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Email verification could not be completed.');
      } finally {
        setPending(false);
      }
  }

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        background: c.bg,
        color: c.text1,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <Logo size={30} />
        </div>

        <div
          style={{
            width: '100%',
            background: c.surface1,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            padding: '24px 22px',
            textAlign: 'center',
          }}
        >
          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 18,
              margin: '0 0 8px',
              color: c.text1,
            }}
          >
            Verify your email
          </h1>
          <p style={{ fontSize: 12, color: c.text3, margin: '0 0 16px' }}>
            Choose your password to confirm your email and secure your account.
          </p>
          <form onSubmit={(event) => void handleVerify(event)}>
            <TextField label="New password" type="password" value={newPassword} onChange={setNewPassword} />
            <TextField label="Confirm password" type="password" value={confirmPassword} onChange={setConfirmPassword} />
            <Btn full type="submit" disabled={pending}>{pending ? 'Verifying…' : 'Verify email and continue'}</Btn>
          </form>
          {error && (
            <>
              <p role="alert" style={{ fontSize: 12, color: c.danger, margin: '0 0 14px' }}>
                {error}
              </p>
              <Btn full type="button" onClick={() => navigate(paths.verifyEmailPending, { replace: true })}>
                Back to verification
              </Btn>
            </>
          )}
        </div>

        <LegalFooter marginTop={20} centered />
      </div>
    </div>
  );
}
